import React, {useRef, useState} from 'react'

import {getTheme} from '_metronic/partials'

interface FileUploadInputProps {
  onFileSelect: (file: File, preview: string) => void
  disabled?: boolean
}

const FileUploadInput: React.FC<FileUploadInputProps> = ({onFileSelect, disabled = false}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isDark = getTheme() === 'dark'

  const handleFileSelect = (file: File) => {
    setError(null)
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('File size exceeds 2MB limit')
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = String(reader.result || '')
      setPreview(dataUrl)
      setSelectedFile(file)
    }
    reader.readAsDataURL(file)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  if (preview && selectedFile) {
    return (
      <div
        style={{
          padding: '12px',
          borderTop: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
          backgroundColor: isDark ? '#1f2937' : '#fff',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '16px',
          }}
        >
          <img
            src={preview}
            alt='Preview'
            style={{
              maxWidth: '150px',
              maxHeight: '150px',
              borderRadius: '8px',
              objectFit: 'cover',
              border: isDark ? '2px solid #374151' : '2px solid #e5e7eb',
            }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'center',
          }}
        >
          <button
            onClick={() => {
              setPreview(null)
              setSelectedFile(null)
              setError(null)
              if (fileInputRef.current) {
                fileInputRef.current.value = ''
              }
            }}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              backgroundColor: isDark ? '#dc2626' : '#dc2626',
              color: '#ffffff',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? '#b91c1c' : '#b91c1c'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#dc2626'
            }}
          >
            Try another
          </button>
          <button
            onClick={() => {
              if (preview && selectedFile) {
                onFileSelect(selectedFile, preview)
              }
            }}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              backgroundColor: isDark ? '#007bff' : '#007bff',
              color: '#ffffff',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? '#0056b3' : '#0056b3'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#007bff'
            }}
          >
            Upload image
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        padding: '12px',
        borderTop: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
        backgroundColor: isDark ? '#1f2937' : '#fff',
      }}
    >
      <input
        ref={fileInputRef}
        type='file'
        accept='image/*'
        style={{display: 'none'}}
        onChange={handleInputChange}
        disabled={disabled}
      />
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: isDark
            ? isDragging
              ? '2px dashed #60a5fa'
              : '2px dashed #4b5563'
            : isDragging
              ? '2px dashed #007bff'
              : '2px dashed #0066cc',
          borderRadius: '8px',
          padding: '32px 16px',
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: isDark
            ? isDragging
              ? '#1e3a8a'
              : '#111827'
            : isDragging
              ? '#f0f8ff'
              : '#fafbfc',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.2s',
        }}
      >
        <div style={{marginBottom: '12px'}}>
          <i
            className='fas fa-cloud-upload-alt'
            style={{
              fontSize: '32px',
              color: isDark ? (isDragging ? '#60a5fa' : '#9ca3af') : '#0066cc',
            }}
          ></i>
        </div>
        <div
          style={{
            fontSize: '14px',
            fontWeight: 500,
            color: isDark ? '#e5e7eb' : '#333',
          }}
        >
          Click or drag file to this area to upload
        </div>
        <div
          style={{
            fontSize: '12px',
            color: isDark ? '#9ca3af' : '#999',
            marginTop: '8px',
          }}
        >
          Support for a single or bulk upload. Maximum file size 2MB.
        </div>
        {error && (
          <div
            style={{
              fontSize: '12px',
              color: '#dc2626',
              marginTop: '8px',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  )
}

export default FileUploadInput
